import Delegate from "ftdomdelegate";

import Form from "../tools/form";
import nodeListToArray from "../tools/nodeListToArray";
import Currency from "../tools/currency";
import Popover from "./popover";
import VariantSelector from "./variant-selector";

export default class ProductVariants {
  constructor(container, options) {
    var _this = this;

    this.element = container;
    this.delegateElement = new Delegate(this.element);
    this.options = options;

    var jsonData = JSON.parse(
      this.element.querySelector("[data-product-json]").innerHTML
    );
    this.productData = jsonData["product"];
    this.variantsInventories = jsonData["inventories"] || {};
    this.masterSelector = this.element.querySelector(
      "#product-select-" + this.productData["id"]
    );

    // We init value with the first selected variant
    this.productData["variants"].forEach(function (variant) {
      if (variant["id"] === jsonData["selected_variant_id"]) {
        _this.currentVariant = variant;
        _this.option1 = variant["option1"];
        _this.option2 = variant["option2"];
        _this.option3 = variant["option3"];
      }
    });

    this._attachListeners();
    this._createSelectors();
  }

  destroy() {
    this.delegateElement.off("click");
    this.formPopovers.forEach(function (popover) {
      return popover.destroy();
    });
    this.formVariantSelectors.forEach(function (selector) {
      return selector.destroy();
    });
  }
  _attachListeners() {
    this.delegateElement.on(
      "click",
      '[data-action="add-to-cart"]',
      this._addToCart.bind(this)
    );
    this.delegateElement.on(
      "click",
      '[data-action="decrease-quantity"]',
      this._decreaseQuantity.bind(this)
    );
    this.delegateElement.on(
      "click",
      '[data-action="increase-quantity"]',
      this._increaseQuantity.bind(this)
    );
    this.delegateElement.on(
      "change",
      '[name="quantity"]',
      this._validateQuantity.bind(this)
    );

    // Hook when a radio button change
    this.delegateElement.on(
      "change",
      '.productForm__option [type="radio"]',
      this._onOptionChanged.bind(this)
    );
  }

  /**
   * Selectors can either be popovers or dedicated variant selectors. We therefore pre-create them all here
   */

  _createSelectors() {
    var _this6 = this;

    // Create the instances for each selector
    this.formPopovers = [];
    this.formVariantSelectors = [];

    nodeListToArray(this.element.querySelectorAll(".optionSelector")).forEach(
      function (item) {
        var popover = new Popover(item, {
          preferredPosition: "left",
          onValueChanged: _this6._onOptionChanged.bind(_this6)
        });
        _this6.formPopovers.push(popover);
      }
    );

    nodeListToArray(this.element.querySelectorAll(".variantSelector")).forEach(
      function (item) {
        var variantSelector = new VariantSelector(item, {
          onValueChanged: _this6._onOptionChanged.bind(_this6)
        });
        _this6.formVariantSelectors.push(variantSelector);
      }
    );
  }

  /**
   * ---------------------------------------------------------------------------------------------------
   * CODE THAT HANDLE VARIANT CHANGES IN THE FRONT
   *
   * Please note that this code is highly dependant on the markup and classes, so make sure to NOT
   * edit this code
   * ---------------------------------------------------------------------------------------------------
   */

  /**
   * This callback is called whenever the variant changes and allows to update data about the active variant
   */

  _onVariantChanged(previousVariant, newVariant) {
    // 1st: the prices
    this._updateProductPrices(newVariant, previousVariant);

    // 2nd: update inventory
    this._updateInventory(newVariant, previousVariant);

    // 3rd: update SKU
    this._updateSku(newVariant, previousVariant);

    // 4th: the add to cart button
    this._updateAddToCartButton(newVariant, previousVariant);

    if (window.theme.currencyConversionEnabled) {
      Currency.convertAll(this.element);
    }

    // Finally, we send an event so that other system could hook and do their own logic
    this.element.dispatchEvent(
      new CustomEvent("variant:changed", {
        bubbles: true,
        detail: { variant: newVariant, previousVariant: previousVariant }
      })
    );
  }

  /**
   * Update the prices (optionally showing compare at price)
   */

  _updateProductPrices(newVariant, previousVariant) {
    var productMetaPrices = this.element.querySelector(
      ".productMeta__priceList"
    );
    if (!newVariant) {
      productMetaPrices.style.display = "none";
    } else {
      if (
        previousVariant &&
        previousVariant["price"] === newVariant["price"] &&
        previousVariant["compare_at_price"] === newVariant["compare_at_price"]
      ) {
        return; // The price do not have changed so let's return to avoid changing the DOM for nothing
      }

      productMetaPrices.innerHTML = "";

      if (newVariant["compare_at_price"] > newVariant["price"]) {
        productMetaPrices.innerHTML +=
          '<span class="productMeta__price price price--highlight text-6 color-heading tracking-normal" data-money-convertible>' +
          Currency.formatMoney(newVariant["price"], window.theme.moneyFormat) +
          "</span>";
        productMetaPrices.innerHTML +=
          '<span class="productMeta__price price price--compareAt text--subdued text-7 line-through" data-money-convertible>' +
          Currency.formatMoney(
            newVariant["compare_at_price"],
            window.theme.moneyFormat
          ) +
          "</span>";
      } else {
        productMetaPrices.innerHTML +=
          '<span class="productMeta__price price text-6 color-heading tracking-normal" data-money-convertible>' +
          Currency.formatMoney(newVariant["price"], window.theme.moneyFormat) +
          "</span>";
      }

      productMetaPrices.style.display = "";
    }
  }

  /**
   * Update the inventory (if needed)
   */

  _updateInventory(newVariant) {
    if (!this.options["showInventoryQuantity"]) {
      return;
    }

    var productFormInventory = this.element.querySelector(
        ".productForm__inventory"
      ),
      variantInventory = newVariant
        ? this.variantsInventories[newVariant["id"]]
        : null;

    if (
      !newVariant ||
      null === variantInventory["inventory_management"] ||
      variantInventory["inventory_quantity"] <= 0 ||
      (this.options["inventoryQuantityThreshold"] > 0 &&
        variantInventory["inventory_quantity"] >
          this.options["inventoryQuantityThreshold"])
    ) {
      productFormInventory.style.display = "none";
    } else {
      productFormInventory.textContent = variantInventory["inventory_message"];
      productFormInventory.style.display = "";
    }
  }

  /**
   * Update the SKU
   */

  _updateSku(newVariant) {
    if (!this.options["showSku"] || !newVariant) {
      return;
    }

    var productSkuNumber = this.element.querySelector(
      ".productMeta__skuNumber"
    );

    if (productSkuNumber && newVariant["sku"]) {
      productSkuNumber.innerText = newVariant["sku"];
    }
  }

  /**
   * Update the add to cart
   */

  _updateAddToCartButton(newVariant) {
    var addToCartButton = this.element.querySelector(
        ".jsProductForm__addToCart"
      ),
      shopifyPaymentButton = this.element.querySelector(
        ".shopify-payment-button"
      ),
      newButton = document.createElement("button");

    newButton.setAttribute("type", "submit");
    newButton.className = `jsProductForm__addToCart button button--primary ${
      this.options["hasSampleProduct"]
        ? "buttonGroup__item buttonGroup__item--expand min-w-full sm:min-w-0"
        : "jsProductForm__addToCart button--primary button buttonGroup__item buttonGroup__item--expand min-w-full sm:min-w-0"
    }`;

    if (!newVariant) {
      newButton.setAttribute("disabled", "disabled");
      newButton.removeAttribute("data-action");
      newButton.innerHTML = window.languages.productFormUnavailable;
    } else {
      if (newVariant["available"]) {
        newButton.removeAttribute("disabled");
        newButton.setAttribute("data-action", "add-to-cart");

        if (
          undefined === this.options["showPriceInButton"] ||
          this.options["showPriceInButton"]
        ) {
          newButton.innerHTML =
            "\n            <span>" +
            window.languages.productFormAddToCart +
            '</span>\n            <span class="button__separatorDot"></span>\n            <span data-money-convertible>' +
            Currency.formatMoney(
              newVariant["price"],
              window.theme.moneyFormat
            ) +
            "</span>\n          ";
        } else {
          newButton.innerHTML =
            "<span>" + window.languages.productFormAddToCart + "</span>";
        }
      } else {
        newButton.setAttribute("disabled", "disabled");
        newButton.removeAttribute("data-action");
        newButton.innerHTML = window.languages.productFormSoldOut;
      }
    }

    if (this.options["showPaymentButton"] && shopifyPaymentButton) {
      if (!newVariant || !newVariant["available"]) {
        shopifyPaymentButton.style.display = "none";
      } else {
        shopifyPaymentButton.style.display = "block";
      }
    }

    // We replace the HTML instead of editing as it prevents for the CSS transition to show up
    addToCartButton.parentNode.replaceChild(newButton, addToCartButton);
  }

  /**
   * ---------------------------------------------------------------------------------------------------
   * INTERNAL CODE THAT HANDLE VARIANT CHANGES
   * ---------------------------------------------------------------------------------------------------
   */

  /**
   * Whenever an option is changed, this code fetch the corresponding active variant
   */

  _onOptionChanged(newValue, target, activator) {
    // We change the value associated with the activator, and check if we have a color swatch
    if (activator) {
      this["option" + target.getAttribute("data-option-position")] = newValue;
      activator.querySelector(".productForm__selectedValue").innerHTML =
        newValue;
    } else {
      this["option" + target.getAttribute("data-option-position")] =
        target.value;
      var selectedValue = target
        .closest(".productForm__option")
        .querySelector(".productForm__selectedValue");

      if (selectedValue) {
        selectedValue.innerHTML = target.value;
      }
    }

    // Finally, we get the new variant
    var previousVariant = this.currentVariant;
    this.currentVariant = this._getCurrentVariantFromOptions();

    this._onVariantChanged(previousVariant, this.currentVariant);

    if (this.currentVariant) {
      if (this.options["enableHistoryState"]) {
        this._updateHistoryState(this.currentVariant);
      }

      // We need to modify the hidden select that contain the id attribute as well
      this.masterSelector
        .querySelector("[selected]")
        .removeAttribute("selected");
      this.masterSelector
        .querySelector('[value="' + this.currentVariant["id"] + '"]')
        .setAttribute("selected", "selected");
    }
  }

  /**
   * Get the active variant based on the options
   */

  _getCurrentVariantFromOptions() {
    var _this7 = this;

    var found = false;

    this.productData["variants"].forEach(function (variant) {
      if (
        variant["option1"] === _this7.option1 &&
        variant["option2"] === _this7.option2 &&
        variant["option3"] === _this7.option3
      ) {
        found = variant;
      }
    });

    return found || null;
  }

  /**
   * Update the history state for browsers that support it
   */

  _updateHistoryState(variant) {
    if (!history.replaceState) {
      return;
    }

    var newUrl =
      window.location.protocol +
      "//" +
      window.location.host +
      window.location.pathname +
      "?variant=" +
      variant.id;

    window.history.replaceState({ path: newUrl }, "", newUrl);
  }

  /**
   * ---------------------------------------------------------------------------------------------------
   * INTERNAL CODE THAT HANDLE PRODUCT ADD TO CART
   * ---------------------------------------------------------------------------------------------------
   */

  _addToCart(event) {
    var _this8 = this;
    const isSample = event.target.classList.contains('jsProductForm__addSample');
    if (!this.options["useAjaxCart"]) {
      return; // When using a cart type of page, we just simply redirect to the cart page
    }

    event.preventDefault(); // Prevent form to be submitted

    if(isSample){
      // First, we switch the status of the button
      document.dispatchEvent(new CustomEvent("theme:loading:start"));

      let quantitySample = this.element.querySelector('form[action^="/cart/add"] [name="quantity"]');

      
      let getSampleVariant = event.target.getAttribute("data-variant-id"); 
      // Then we add the product in Ajax
      var formElementSample = {
        "form_type":"product",
        "utf8":"✓",
        "id":getSampleVariant,
        "quantity":quantitySample
      };
      console.log(formElementSample);
      fetch("/cart/add.js", {
        body: JSON.stringify(formElementSample),
        credentials: "same-origin",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest" // This is needed as currently there is a bug in Shopify that assumes this header
        }
      }).then(function (response) {
        document.dispatchEvent(new CustomEvent("theme:loading:end"));
        if (response.ok) {
          // We simply trigger an event so the mini-cart can re-render
          _this8.element.dispatchEvent(
            new CustomEvent("product:added", {
              bubbles: true,
              detail: {
                variant: getSampleVariant,
                quantity: parseInt(quantitySample.value)
              }
            })
          );
        } else {
          response.json().then(function (content) {
            var errorMessageElement = document.createElement("span");
            errorMessageElement.className =
              "productForm__error alert alert--error";
            errorMessageElement.innerHTML = content["description"];
            event.target.removeAttribute("disabled");
            event.target.insertAdjacentElement(
              "afterend",
              errorMessageElement
            );
            setTimeout(function () {
              errorMessageElement.remove();
            }, 2500);
          });
        }
      });
      return;
    }

    var addToCartButton = this.element.querySelector(
      ".jsProductForm__addToCart"
    );

    // First, we switch the status of the button 
    document.dispatchEvent(new CustomEvent("theme:loading:start"));

    // Then we add the product in Ajax
    var formElement = this.element.querySelector('form[action^="/cart/add"]');

    fetch("/cart/add.js", {
      body: JSON.stringify(Form.serialize(formElement)),
      credentials: "same-origin",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest" // This is needed as currently there is a bug in Shopify that assumes this header
      }
    }).then(function (response) {
      document.dispatchEvent(new CustomEvent("theme:loading:end"));
      if (response.ok) {
        addToCartButton.removeAttribute("disabled");
        // We simply trigger an event so the mini-cart can re-render
        _this8.element.dispatchEvent(
          new CustomEvent("product:added", {
            bubbles: true,
            detail: {
              variant: _this8.currentVariant,
              quantity: parseInt(
                formElement.querySelector('[name="quantity"]').value
              )
            }
          })
        );
      } else {
        response.json().then(function (content) {
          var errorMessageElement = document.createElement("span");
          errorMessageElement.className =
            "productForm__error alert alert--error";
          errorMessageElement.innerHTML = content["description"];
          addToCartButton.removeAttribute("disabled");
          addToCartButton.insertAdjacentElement(
            "afterend",
            errorMessageElement
          );
          setTimeout(function () {
            errorMessageElement.remove();
          }, 2500);
        });
      }
    });

    event.preventDefault();
  }

  /**
   * ---------------------------------------------------------------------------------------------------
   * OTHER
   * ---------------------------------------------------------------------------------------------------
   */

  /**
   * When using the quantity selector, this can be used to decrease the quantity (be ensuring it won't be lower than 1)
   */

  _decreaseQuantity(event, target) {
    target.nextElementSibling.value = Math.max(
      parseInt(target.nextElementSibling.value) - 1,
      1
    );
  }

  /**
   * When using the quantity selector, this can be used to increase the quantity
   */

  _increaseQuantity(event, target) {
    target.previousElementSibling.value =
      parseInt(target.previousElementSibling.value) + 1;
  }

  /**
   * Make sure the quantity does not go below when manually changed
   */

  _validateQuantity(event, target) {
    target.value = Math.max(parseInt(target.value) || 1, 1);
  }
}
